/**
 * Hubs config + item catalogue — the single source the entryway, Hub pages, Hub
 * header (breadcrumb + switcher) and Home quick links read.
 *
 * Methodology: docs/handoffs/07-hubs.md. The rules this file encodes:
 *   1. Every item has exactly ONE home Hub (`home`): the department accountable
 *      for its data. The home drives the breadcrumb and the access UI.
 *   2. Other Hubs can list it too (`alsoIn`). Same item, same URL, never a copy.
 *   3. One URL per item (`route`), wherever it's opened from. Routing stays flat.
 *   4. Access is per item. A Hub shows only items the user can open, and a Hub
 *      with nothing visible doesn't show on the entryway (Hubs whose catalogue is
 *      all "coming soon" still show, so they can display their roadmap).
 *   5. Clients are a filter, not a Hub (`clientId` on client-specific items feeds
 *      the client switcher in the Hub header).
 *   6. Unreleased items are `status: 'coming-soon'`.
 *
 * DWA Internal dashboards are NOT re-declared here: they come from
 * `pages/dwaInternalCatalogue.ts` (department slug = home Hub id, `alsoIn` kept),
 * so that file stays the one place a dashboard is added. Everything that is not a
 * DWA Internal dashboard (client CRM dashboards, admin pages, training pages,
 * coming-soon placeholders) is declared in `EXTRA_ITEMS` below.
 *
 * Visibility here is UX only. The backend stays the security boundary for every
 * API call an item makes.
 */
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faHouse,
  faComments,
  faBook,
  faAddressBook,
  faPalette,
  faBullhorn,
  faGears,
  faGlobe,
  faArrowTrendUp,
  faUserShield,
  faSackDollar,
} from '@fortawesome/free-solid-svg-icons';
import type { User, UserRole } from '../types';
import { DWA_INTERNAL_DEPARTMENTS } from '../pages/dwaInternalCatalogue';
import { canSeeDwaDashboard } from '../pages/dwaInternalAccess';

// ─── Hubs ────────────────────────────────────────────────────────────────────

export type HubId =
  | 'home'
  | 'lupa'
  | 'knowledgebase'
  | 'accounts'
  | 'creative'
  | 'media'
  | 'operations'
  | 'web'
  | 'growth'
  | 'finance'
  | 'admin';

export type HubStatus = 'live' | 'coming-soon';

export interface Hub {
  id: HubId;
  title: string;
  /** One line for the entryway tile and the Hub page subtitle. */
  description: string;
  icon: IconDefinition;
  /** Where the Hub's tile goes. Department/shared Hubs use `/hubs/<id>`; Lupa is the chat page. */
  route: string;
  /** Roles that can see the Hub at all. Items inside are gated again, one by one. */
  roles: UserRole[];
  /**
   * 'coming-soon' marks a Hub whose items are all still on the roadmap. It still
   * opens (07-hubs §2 rule 6: empty Hubs show their coming-soon items).
   */
  status: HubStatus;
  kind: 'shared' | 'department';
}

const DWA_ROLES: UserRole[] = ['dwa_admin', 'dwa_standard'];
const ADMIN_ONLY: UserRole[] = ['dwa_admin'];

/** Entryway order. Shared Hubs first, then departments (alphabetical), then Admin. */
export const HUBS: Hub[] = [
  {
    id: 'home',
    title: 'Home',
    description: 'Your day at a glance: notifications, recent Lupa chats and quick links.',
    icon: faHouse,
    route: '/hubs/home',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'shared',
  },
  {
    id: 'lupa',
    title: 'Lupa',
    description: 'Full-page chat with Lupa, with your conversation history.',
    icon: faComments,
    route: '/lupa',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'shared',
  },
  {
    // Team SOPs and documents, searched from DWA's Google shared drive (read-only
    // index; editing stays in Drive). KB knowledgebase.md.
    id: 'knowledgebase',
    title: 'Knowledgebase',
    description: "Search the team's SOPs and documents from the DWA shared drive.",
    icon: faBook,
    route: '/hubs/knowledgebase',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'shared',
  },
  {
    id: 'accounts',
    title: 'Accounts',
    description: 'Client records, onboarding, brands and client CRM dashboards.',
    icon: faAddressBook,
    route: '/hubs/accounts',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    id: 'creative',
    title: 'Creative',
    description: 'Creative Scorecard, plus the brand and banner tools Creative relies on.',
    icon: faPalette,
    route: '/hubs/creative',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    id: 'media',
    title: 'Media',
    description: 'Client CRM dashboards and the reporting Media uses.',
    icon: faBullhorn,
    route: '/hubs/media',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Team capacity, service health and agency-wide numbers.',
    icon: faGears,
    route: '/hubs/operations',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    id: 'web',
    title: 'Web',
    description: 'Client site monitors, banners, backups and site-file tools.',
    icon: faGlobe,
    route: '/hubs/web',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    id: 'growth',
    title: 'Growth',
    description: 'Sales and new business: HubSpot pipeline, leads, win/loss and CRM health.',
    icon: faArrowTrendUp,
    route: '/hubs/growth',
    roles: DWA_ROLES,
    status: 'live',
    kind: 'department',
  },
  {
    // Placeholder until the Ramp integration (PR #128) ships the real Finance Hub,
    // which is gated by the per-user finance permission (a sub-permission of admin).
    id: 'finance',
    title: 'Finance',
    description: 'Company spend and accounting from Ramp: spend, vendors, cards, reimbursements and bills.',
    icon: faSackDollar,
    route: '/hubs/finance',
    roles: ADMIN_ONLY,
    status: 'coming-soon',
    kind: 'shared',
  },
  {
    id: 'admin',
    title: 'Admin',
    description: 'Users and access, email recipients, Lupa configuration and Lupa training.',
    icon: faUserShield,
    route: '/hubs/admin',
    roles: ADMIN_ONLY,
    status: 'live',
    kind: 'shared',
  },
];

export function findHub(id: string | undefined): Hub | undefined {
  return HUBS.find((h) => h.id === id);
}

// ─── Items ───────────────────────────────────────────────────────────────────

export type HubItemStatus = 'live' | 'coming-soon';

export interface HubItem {
  /** Globally unique. DWA Internal dashboards use their dashboard slug. */
  id: string;
  title: string;
  description?: string;
  /** Planned scope, shown on coming-soon cards / placeholders. */
  scope?: string[];
  /** The one Hub accountable for this item's data. */
  home: HubId;
  /** Other Hubs that list it too, marked "from <home>". */
  alsoIn?: HubId[];
  /**
   * Canonical URL (path, optionally with a query). Omitted for coming-soon items
   * that have no page yet.
   */
  route?: string;
  status: HubItemStatus;
  /** Roles that may open it. Omitted = all DWA roles. */
  roles?: UserRole[];
  /** Set for DWA Internal dashboards — gated by `canSeeDwaDashboard` too. */
  dwaDashboardSlug?: string;
  /** Client-specific item: feeds the client switcher in the Hub header. */
  clientId?: string;
  clientLabel?: string;
}

const DEPARTMENT_HUB_IDS: HubId[] = ['accounts', 'creative', 'media', 'operations', 'web', 'growth'];

function asHubId(slug: string): HubId | undefined {
  return DEPARTMENT_HUB_IDS.find((id) => id === slug);
}

/** DWA Internal dashboards, mapped into Hub items (department slug → home Hub). */
const DASHBOARD_ITEMS: HubItem[] = DWA_INTERNAL_DEPARTMENTS.flatMap((dept) => {
  const home = asHubId(dept.slug);
  if (!home) return [];
  return dept.dashboards.map<HubItem>((d) => ({
    id: d.slug,
    title: d.name,
    description: d.description,
    scope: d.scope,
    home,
    alsoIn: (d.alsoIn ?? []).map(asHubId).filter((h): h is HubId => Boolean(h)),
    route: `/dashboard/${d.slug}`,
    status: d.status,
    dwaDashboardSlug: d.slug,
  }));
});

/**
 * Everything that is not a DWA Internal dashboard. Add new tools/pages here with
 * a `home` and, if other departments act on them, `alsoIn`.
 */
const EXTRA_ITEMS: HubItem[] = [
  // Media — client CRM dashboards (one per client; the header's client switcher
  // jumps between them). Home is Media (Jon, 2026-11-03): Media reports on lead flow.
  {
    id: 'client-crm-plc',
    title: 'Priority Life Care CRM',
    description: 'WelcomeHome leads, tours, move-ins and occupancy for Priority Life Care.',
    home: 'media',
    route: '/dashboard/priority-life-care',
    status: 'live',
    clientId: 'plc',
    clientLabel: 'Priority Life Care',
  },
  {
    id: 'client-crm-morningstar',
    title: 'MorningStar CRM',
    description: 'Aline occupancy, leads and move-ins for MorningStar Senior Living.',
    home: 'media',
    route: '/dashboard/morningstar-senior-living',
    status: 'live',
    clientId: 'morningstar',
    clientLabel: 'MorningStar Senior Living',
  },

  // Operations — Morning Howl, the daily stand-up board (KB morning-howl.md).
  {
    id: 'operations-morning-howl',
    title: 'Morning Howl',
    description: 'The daily stand-up: randomize who goes first, check people off, and track who stays after.',
    home: 'operations',
    route: '/morning-howl',
    status: 'live',
  },
  // Lupa usage & costs lives in Admin only (Jon, 2026-11-03: "strictly admin"; it was
  // in Operations too). The id keeps its old prefix so existing pins still resolve.
  {
    id: 'operations-usage-costs',
    title: 'Lupa usage & costs',
    description: 'Ingest and test-run costs from Lupa Admin. Chat token costs are not recorded yet.',
    home: 'admin',
    route: '/lupa-admin?tab=costs',
    status: 'live',
    roles: ADMIN_ONLY,
  },

  // Web — site-file generators (handoffs 03 / 04).
  {
    id: 'web-schema-generator',
    title: 'Schema generator',
    description: 'Form → draft Schema Editor import JSON for a client site (handoff 03).',
    home: 'web',
    status: 'coming-soon',
  },
  {
    id: 'web-llms-generator',
    title: 'llms.txt generator',
    description: 'Form → draft llms.txt and llms-full.txt for a client site (handoff 04).',
    home: 'web',
    status: 'coming-soon',
  },

  // Finance — coming soon (Ramp, PR #128); admin-only like the Hub.
  {
    id: 'finance-ramp',
    title: 'Ramp spend & accounting',
    description: 'Spend overview, vendors & subscriptions, cards & people, reimbursements & bills, receipts & hygiene.',
    home: 'finance',
    status: 'coming-soon',
    roles: ADMIN_ONLY,
  },

  // Growth — the HubSpot dashboards are DWA Internal dashboards
  // (dwaInternalCatalogue.ts, department 'growth'), so nothing is declared here.

  // Lupa training lives in Admin (Jon, 2026-11-03): it's admin work on Lupa, not team
  // training, and "Knowledgebase" is reserved for the team's SOP Hub.
  {
    id: 'training-skills-library',
    title: 'Lupa skills library',
    description: 'Reusable Lupa skills written by the team (handoff 05).',
    home: 'admin',
    status: 'coming-soon',
    roles: ADMIN_ONLY,
  },
  {
    id: 'training-knowledge-base',
    title: 'Lupa documents',
    description: 'The documents Lupa searches: upload, sync and inspect chunks.',
    home: 'admin',
    route: '/lupa-admin?tab=documents',
    status: 'live',
    roles: ADMIN_ONLY,
  },
  {
    id: 'training-tests',
    title: 'Lupa test sets & runs',
    description: 'Retrieval test sets, generated questions and run results.',
    home: 'admin',
    route: '/lupa-admin?tab=tests',
    status: 'live',
    roles: ADMIN_ONLY,
  },
  {
    id: 'training-feedback-review',
    title: 'Lupa feedback review',
    description: 'Flagged Lupa answers, corrections and re-tests.',
    home: 'admin',
    route: '/feedback-review',
    status: 'live',
    roles: ADMIN_ONLY,
  },

  // Admin
  {
    id: 'admin-lupa-admin',
    title: 'Lupa Admin',
    description: 'Overview of documents, search, errors and settings.',
    home: 'admin',
    route: '/lupa-admin',
    status: 'live',
    roles: ADMIN_ONLY,
  },
  {
    id: 'admin-users',
    title: 'Users & access',
    description: 'Accounts, roles, client and dashboard access, and email recipients.',
    home: 'admin',
    route: '/lupa-admin?tab=users',
    status: 'live',
    roles: ADMIN_ONLY,
  },
  {
    // Which DWA shared-drive folders the Knowledgebase Hub indexes (KB knowledgebase.md).
    id: 'admin-kb-sources',
    title: 'Knowledgebase sources',
    description: 'Choose which shared-drive folders the Knowledgebase indexes and searches.',
    home: 'admin',
    route: '/knowledgebase-sources',
    status: 'live',
    roles: ADMIN_ONLY,
  },
  {
    id: 'admin-settings',
    title: 'Configuration',
    description: 'Lupa configuration settings.',
    home: 'admin',
    route: '/lupa-admin?tab=settings',
    status: 'live',
    roles: ADMIN_ONLY,
  },
];

export const HUB_ITEMS: HubItem[] = [...DASHBOARD_ITEMS, ...EXTRA_ITEMS];

// ─── Visibility ──────────────────────────────────────────────────────────────

export function isDwaUser(user: User | null | undefined): boolean {
  return user?.role === 'dwa_admin' || user?.role === 'dwa_standard';
}

export function canSeeItem(user: User | null | undefined, item: HubItem): boolean {
  if (!user || !isDwaUser(user)) return false;
  if (item.roles && !item.roles.includes(user.role)) return false;
  if (item.dwaDashboardSlug && !canSeeDwaDashboard(user, item.dwaDashboardSlug)) return false;
  return true;
}

export interface HubListing {
  item: HubItem;
  /** True when this Hub is the item's home; false for "also in" entries. */
  isHome: boolean;
}

/** Items in a Hub's catalogue regardless of user: home items first, then "also in". */
export function catalogueForHub(hubId: HubId): HubListing[] {
  const own = HUB_ITEMS.filter((i) => i.home === hubId).map((item) => ({ item, isHome: true }));
  const mirrored = HUB_ITEMS.filter((i) => i.home !== hubId && i.alsoIn?.includes(hubId)).map(
    (item) => ({ item, isHome: false }),
  );
  return [...own, ...mirrored];
}

/** Items a user sees in a Hub: home items, then "also in" items, access-filtered. */
export function itemsForHub(hubId: HubId, user: User | null | undefined): HubListing[] {
  return catalogueForHub(hubId).filter(({ item }) => canSeeItem(user, item));
}

/** Hubs with their own built-in content (not just an item list). */
const CONTENT_HUBS: HubId[] = ['home', 'lupa', 'knowledgebase'];

export function canSeeHub(user: User | null | undefined, hub: Hub): boolean {
  if (!user || !isDwaUser(user)) return false;
  if (!hub.roles.includes(user.role)) return false;
  if (CONTENT_HUBS.includes(hub.id)) return true;
  const catalogue = catalogueForHub(hub.id);
  // Rule 4: a Hub whose items are all hidden from this user doesn't show.
  return catalogue.some(({ item }) => canSeeItem(user, item));
}

/** Hubs for the entryway and the Hub switcher. Client-role users get none. */
export function visibleHubs(user: User | null | undefined): Hub[] {
  return HUBS.filter((hub) => canSeeHub(user, hub));
}

// ─── Location → Hub / item (breadcrumb, switcher, page context) ──────────────

export function findItem(id: string): HubItem | undefined {
  return HUB_ITEMS.find((i) => i.id === id);
}

/**
 * The item a URL belongs to. Exact match on path + query first, then path only
 * (so `/lupa-admin?tab=chunks` still resolves to Lupa Admin).
 */
export function findItemByLocation(pathname: string, search = ''): HubItem | undefined {
  const tab = new URLSearchParams(search).get('tab');
  const withTab = tab ? `${pathname}?tab=${tab}` : pathname;
  return (
    HUB_ITEMS.find((i) => i.route === withTab) ?? HUB_ITEMS.find((i) => i.route === pathname)
  );
}

export interface HubLocation {
  hub?: Hub;
  item?: HubItem;
}

export function resolveHubLocation(pathname: string, search = ''): HubLocation {
  const hubMatch = /^\/hubs\/([^/]+)\/?$/.exec(pathname);
  if (hubMatch) return { hub: findHub(hubMatch[1]) };
  if (pathname === '/lupa' || pathname.startsWith('/lupa/')) return { hub: findHub('lupa') };
  const item = findItemByLocation(pathname, search);
  if (item) return { hub: findHub(item.home), item };
  return {};
}

/** Items that share a client switcher (client-specific items in the same home Hub). */
export function clientSiblings(item: HubItem): HubItem[] {
  if (!item.clientId) return [];
  return HUB_ITEMS.filter((i) => i.clientId && i.home === item.home && i.route);
}

/** First name for greetings ("Welcome back, Jon"). */
export function greetingName(user: { display_name?: string; email?: string } | null | undefined): string {
  return user?.display_name?.trim().split(/\s+/)[0] || user?.email?.split('@')[0] || 'there';
}
